import { useEffect } from 'react';
import { withWindow } from '@shared/utils/withWindow';

/** GRAPHIC_CLASS_PREFIX in style-applicator/constants.ts. */
const SPRITE_SELECTOR = '[class^="jyse-"]';

type CleanUp = (() => void) | undefined;

/**
 * Keeps the style applicator's art sprites out of CLS on standalone-hero headers.
 *
 * The applicator inserts every sprite laid out by its generated JSS class alone —
 * absolutely positioned inside the frame — and only afterwards writes the `position: fixed`
 * viewport geometry of scroll-fixed art as inline styles. That write is scheduled at
 * insertion time with `setTimeout(0)` → `rAF` ('_a' in applicator.js), so the sprite paints
 * at least one frame in its in-frame position before jumping to its final one, and Chromium
 * scores the difference.
 *
 * Under a standalone hero the frame starts a full viewport below the fold, so the jump spans
 * the whole screen: measured 0.7031 in a single entry on a photo-background event (back-layer
 * art `[0,900,1280,0.1]` → `[0,0,1280,900]` plus both 94px side rails). With an in-flow header
 * the same jump starts near y=0 and scores ~0, which is why this is scoped to the hero layouts.
 *
 * The guard hides each sprite the moment it is inserted — this observer's callback is a
 * microtask of the insertion task, so it runs before the sprite can paint — and reveals it
 * through the same `setTimeout(0)` → `rAF` queue the applicator uses. That is ordering, not
 * timing: the applicator registers its timer while building the fragment, strictly before
 * this callback registers ours, so the reveal always lands after the positioning write, no
 * matter how long the main thread stalls in between (measured 8s apart on a dev server).
 * Sprites the applicator never positions are revealed by the same queue, so nothing depends
 * on guessing which art is scroll-fixed.
 *
 * `visibility: hidden` rather than opacity: invisible elements are exempt from layout-shift
 * tracking, and the reveal is geometry-neutral, so it scores nothing itself.
 *
 * Observes the body of the window this tree renders in, reached through `withWindow` so the
 * DOM is never touched outside a browser: the applicator owns where it injects, and scoping
 * to a React-held ref made an earlier version of this guard silently inert.
 *
 * It stays armed for the layout's lifetime rather than disconnecting once the first batch
 * lands, because `reapplyDesign()` re-runs insert fresh sprites at arbitrary times (lazy
 * header components trigger one after mount). Gating on the applicator's own `loading` state
 * was tried and measured back at 0.80: a cached re-run resolves in microtasks and inserts its
 * sprites before React can commit the state change that would arm the observer. The per-batch
 * work is one selector match per inserted node — a `DocumentFragment` insertion reports its
 * children as the added nodes, and the applicator appends each sprite straight to the
 * fragment, so sprites always arrive as added nodes themselves and never need a subtree scan.
 */
export const useApplicatorSpriteGuard = (enabled: boolean) => {
  useEffect(() => {
    if (!enabled) return;

    // Everything runs against the window this tree renders in — the same one the applicator
    // injects into, including the website-designer preview, where the guest app loads inside
    // the iframe rather than being portalled into the parent document.
    return withWindow<CleanUp>(win => {
      const body = win.document.body;
      if (!body) return undefined;

      const hideUntilPositioned = (sprite: HTMLElement) => {
        sprite.style.visibility = 'hidden';
        win.setTimeout(() => {
          win.requestAnimationFrame(() => {
            sprite.style.visibility = '';
          });
        }, 0);
      };

      const observer = new MutationObserver(mutations => {
        for (const mutation of mutations) {
          for (const node of mutation.addedNodes) {
            if (node instanceof HTMLElement && node.matches(SPRITE_SELECTOR)) hideUntilPositioned(node);
          }
        }
      });
      observer.observe(body, { childList: true, subtree: true });

      return () => observer.disconnect();
    }, undefined);
  }, [enabled]);
};
